import { queryOptions } from '@tanstack/react-query'
import { api } from '@/api/client'
import type {
  ModerationItem,
  Paginated,
  PersonRef,
  RawAuthor,
  RawPoll,
  RawPost,
  RawReportedPoll,
  RawReportedPost,
  UnitRef,
} from '@/api/pending'
import { queryKeys } from '@/api/queryKeys'

/** No residence on file (e.g. an admin posting without a unit). */
const NO_UNIT: UnitRef = { id: 0, number: '—', tower: { id: 0, name: '' } }

function unit(author: RawAuthor): UnitRef {
  if (!author.unit) return NO_UNIT
  return { id: author.unit.id, number: author.unit.number, tower: author.unit.tower ?? { id: 0, name: '' } }
}

function person(author: RawAuthor): PersonRef {
  return {
    id: author.id,
    first_name: author.first_name,
    last_name: author.last_name,
    is_admin: author.is_admin,
  }
}

function fromPost(raw: RawPost | RawReportedPost): ModerationItem {
  const reported = 'report_count' in raw ? raw : undefined
  return {
    id: raw.id,
    kind: 'post',
    title: raw.title,
    body: raw.body,
    category: raw.category,
    author: person(raw.author),
    unit: unit(raw.author),
    created_at: raw.created_at,
    tags: raw.tags,
    report_count: reported?.report_count,
    max_weight: reported?.max_weight,
    top_reason: reported?.top_reason,
  }
}

function fromPoll(raw: RawPoll | RawReportedPoll): ModerationItem {
  const reported = 'report_count' in raw ? raw : undefined
  return {
    id: raw.id,
    kind: 'poll',
    title: raw.question,
    body: '',
    // Polls have no category of their own; the UI shows an "Enquete" badge instead.
    category: 'post',
    author: person(raw.author),
    unit: unit(raw.author),
    created_at: raw.created_at,
    options: raw.options.map((o) => o.text),
    ends_at: raw.expires_at ?? undefined,
    report_count: reported?.report_count,
    max_weight: reported?.max_weight,
    top_reason: reported?.top_reason,
  }
}

export const pendingPostsQuery = (condoId: number) =>
  queryOptions({
    queryKey: queryKeys.posts.pending(condoId),
    queryFn: ({ signal }) =>
      api
        .get<Paginated<RawPost>>('/condominiums/:id/posts/pending', { params: { id: condoId }, signal })
        .then((r) => r.data.map(fromPost)),
  })

export const pendingPollsQuery = (condoId: number) =>
  queryOptions({
    queryKey: queryKeys.polls.pending(condoId),
    queryFn: ({ signal }) =>
      api
        .get<Paginated<RawPoll>>('/condominiums/:id/polls/pending', { params: { id: condoId }, signal })
        .then((r) => r.data.map(fromPoll)),
  })

export const reportedPostsQuery = (condoId: number) =>
  queryOptions({
    queryKey: queryKeys.posts.reported(condoId),
    queryFn: ({ signal }) =>
      api
        .get<Paginated<RawReportedPost>>('/condominiums/:id/posts/reported', {
          params: { id: condoId },
          signal,
        })
        .then((r) => r.data.map(fromPost)),
  })

export const reportedPollsQuery = (condoId: number) =>
  queryOptions({
    queryKey: queryKeys.polls.reported(condoId),
    queryFn: ({ signal }) =>
      api
        .get<Paginated<RawReportedPoll>>('/condominiums/:id/polls/reported', {
          params: { id: condoId },
          signal,
        })
        .then((r) => r.data.map(fromPoll)),
  })

/** Pending posts and polls as one queue, oldest first. */
export function mergeQueue(posts: ModerationItem[], polls: ModerationItem[]): ModerationItem[] {
  return [...posts, ...polls].sort((a, b) => a.created_at.localeCompare(b.created_at))
}
